import React from 'react'
import Image from 'next/image'
import { useRouter } from 'next/router'

import StatBox from '@features/profile/components/StatBox/StatBox'
import ProfileSection from '@features/profile/components/ProfileSection/ProfileSection'
import { Button } from '@components/Buttons'
import { FollowButton } from '@components/FollowButton'
import { useDrawer } from '@hooks/contexts/DrawerContext/DrawerContext'
import { useAuthentication } from '@hooks/useAuthentication'
import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { Avatar } from '@components/Avatar'
import { ChatIcon } from '@components/icons/ChatIcon'
import { SettingIcon } from '@components/icons/SettingIcon'
import { ProfileHeaderProps } from './interface'

export function ProfileHeader({
  userId,
  profilePageSlug,
  bannerImgPath,
  avatarImgPath,
  displayName,
  username,
  aboutMe,
  facebookLink,
  twitterLink,
  isFollowed,
  totalFollowing,
  totalFollower,
  totalWriting,
  totalBookmark,
  totalReading,
  canFollow,
  canChat,
  canSetting,
  isAdmin,
  allowSendMessage,
  email,
  onFollow,
}: ProfileHeaderProps) {
  const router = useRouter()
  const chatDrawer = useDrawer({
    drawer: 'chatDrawer',
    drawerProps: {
      chatWithUser: { id: userId, profilePageSlug, avatarImgPath, displayName },
    },
  })
  const loginModal = useModal({ modal: 'login' })
  const { isAuthenticated } = useAuthentication()

  return (
    <div className='relative pb-[30px]'>
      <div className='flex justify-between'>
        <div className='font-mitr topic-base mb-[20px] mobile:mb-[10px] pb-[180px] mobile:pb-[100px] dark:text-primary'>
          โปรไฟล์
        </div>
        {canSetting && (
          <Button
            className='text-[14px] bg-secondary dark:bg-purple text-white border dark:text-white-2 dark:border-purple'
            type='button'
            icon={<SettingIcon />}
            onClick={() => router.push('/my-profile/setting')}
          >
            ตั้งค่า
          </Button>
        )}
      </div>
      <div className='absolute top-[60px] h-[240px] mobile:w-[calc(100vw-45px)] w-[calc(100vw-40px)] max-w-[1326px] left-[50%] translate-x-[-50%] mobile:h-[72px] rounded-[8px] overflow-hidden'>
        <Image
          src={bannerImgPath || '/images/cover-profile.png'}
          alt='Cover profile'
          layout='fill'
        />
      </div>
      <div className='relative bg-white dark:bg-dark-12 rounded-[8px] px-[30px] pt-[30px] pb-[40px] mobile:px-[16px] mobile:py-[16px] shadow-card border border-gray-4 dark:border-dark-9'>
        <div className='flex items-center gap-[20px] tablet-mobile:grid tablet:grid-cols-3'>
          <div className='flex grow space-x-[20px] tablet:col-span-2'>
            <Avatar
              className='w-[100px]'
              src={avatarImgPath}
              alt='Default User avatar'
            />
            <ProfileSection
              displayName={displayName}
              userName={username}
              facebookLink={facebookLink}
              twitterLink={twitterLink}
              totalFollowing={totalFollowing}
              totalFollower={totalFollower}
              isAdmin={isAdmin}
              email={email}
            />
          </div>
          <StatBox
            className='tablet-mobile:order-last tablet:col-start-1 tablet:col-end-4 tablet:justify-self-center tablet:w-full'
            totalWriting={totalWriting}
            totalBookmark={totalBookmark}
            totalReading={totalReading}
          />
          {(canChat || canFollow) && (
            <div className='flex justify-end space-x-[10px] mobile:space-x-[20px] mobile:justify-center'>
              {canChat && (
                <button
                  className='flex items-center justify-center min-w-[40px] h-[40px] border-gray-4 dark:border-none border rounded-full disabled:opacity-60 disabled:cursor-not-allowed'
                  type='button'
                  onClick={() => {
                    isAuthenticated ? chatDrawer.show() : loginModal.show()
                  }}
                  disabled={!allowSendMessage && !isAdmin}
                >
                  <ChatIcon className='text-primary' width='24' height='24' />
                </button>
              )}
              {canFollow && (
                <FollowButton
                  className='w-[100px] mobile:w-full'
                  isFollowed={!!isFollowed}
                  onClick={() => {
                    if (onFollow) {
                      onFollow()
                    }
                  }}
                />
              )}
            </div>
          )}
        </div>
        {aboutMe && (
          <div className='w-full font-light text-[16px] min-h-[70px] tablet-mobile:min-h-[45px] border-t border-t-gray-3 dark:border-t-purple pt-[29.5px] mt-[29.5px] mobile:pt-[21.5px] mobile:mt-[19.5px]'>
            <span className='text-secondary dark:text-dark-10 whitespace-pre-wrap'>
              {aboutMe}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}
